import { useState } from 'react'
import type { ReactElement } from 'react'

import { useSession } from '../../auth/useSession'
import { Button, ErrorState, InputText, ProductGroupPicker, Spinner, Tag } from '../../components'
import { FreshnessBadge } from '../../layout/FreshnessBadge'
import { trpcMessage } from '../../lib/trpcMessages'
import { trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import { AdminPageHeader, ReadOnlyNote, adminStyles as styles, numberText, parseNonNegative } from '../admin/AdminPage'

type Settings = RouterOutputs['configuration']['pesajeSettings']['get']
type KnownLocations = RouterOutputs['configuration']['pesajeSettings']['knownLocations']
type ProductGroups = RouterOutputs['configuration']['listProductGroups']['data']
type SettingsPatch = Partial<Settings>

/** The API's bounds for the tolerance (`pesajeSettingsSchema`). */
const MAX_TOLERANCE_PCT = 50

type Draft = {
  locationOrder: Array<number>
  /** The groups ticked in the picker, by id: the ones excluded by id or by title. */
  excludedGroups: Array<number>
  tolerance: string
  channel: string
}

type RouteStop = { locationId: number; name: string; known: boolean }

/**
 * The route as it is listed: the ordered locations in their order — one the
 * mirror no longer knows stays, named by its id, so nobody loses it without
 * deciding to — and after them the known ones not on it, by name, which is
 * where the screen puts them too.
 */
export const routeStops = (locationOrder: Array<number>, known: KnownLocations['data']): { ordered: Array<RouteStop>; unordered: Array<RouteStop> } => {
  const nameOf = new Map(known.map((location) => [location.locationId, location.location]))
  const onRoute = new Set(locationOrder)
  return {
    ordered: locationOrder.map((locationId) => {
      const name = nameOf.get(locationId)
      return name === undefined
        ? { locationId, name: `Ubicación ${locationId}`, known: false }
        : { locationId, name, known: true }
    }),
    unordered: known
      .filter((location) => !onRoute.has(location.locationId))
      .map((location) => ({ locationId: location.locationId, name: location.location, known: true }))
      .sort((left, right) => (left.name < right.name ? -1 : left.name > right.name ? 1 : left.locationId - right.locationId)),
  }
}

/** The ids the picker shows ticked: the excluded ids, and the mirror's groups whose title is excluded. */
const tickedGroups = (settings: Settings, groups: ProductGroups): Array<number> => {
  const titled = groups
    .filter((group) => group.title !== null && settings.excludedGroupTitles.includes(group.title))
    .map((group) => group.groupId)
  return [...new Set([...settings.excludedGroupIds, ...titled])]
}

/**
 * The exclusion the ticked groups make. A title stays as a title while its
 * group is ticked, so the defaults keep matching by name; unticking a group
 * drops its title, and a title the mirror has no group for is kept as it was.
 * Every other ticked group goes by id.
 */
const exclusionOf = (settings: Settings, groups: ProductGroups, ticked: Array<number>): Pick<Settings, 'excludedGroupIds' | 'excludedGroupTitles'> => {
  const titleOf = new Map(groups.map((group) => [group.groupId, group.title]))
  const untickedTitles = new Set(groups
    .filter((group) => !ticked.includes(group.groupId) && group.title !== null)
    .map((group) => group.title))
  const excludedGroupTitles = settings.excludedGroupTitles.filter((title) => !untickedTitles.has(title))
  const excludedGroupIds = ticked.filter((groupId) => {
    const title = titleOf.get(groupId)
    return title === undefined || title === null || !excludedGroupTitles.includes(title)
  })
  return { excludedGroupIds, excludedGroupTitles }
}

const sameList = <T,>(left: Array<T>, right: Array<T>): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index])

type Parsed = { patch: SettingsPatch; invalid: Array<string> }

/** The keys that changed, and the labels of the fields that do not parse. */
const patchOf = (settings: Settings, groups: ProductGroups, draft: Draft): Parsed => {
  const patch: SettingsPatch = {}
  const invalid: Array<string> = []
  if (!sameList(draft.locationOrder, settings.locationOrder)) {
    patch.locationOrder = draft.locationOrder
  }
  const exclusion = exclusionOf(settings, groups, draft.excludedGroups)
  if (!sameList(exclusion.excludedGroupIds, settings.excludedGroupIds)) {
    patch.excludedGroupIds = exclusion.excludedGroupIds
  }
  if (!sameList(exclusion.excludedGroupTitles, settings.excludedGroupTitles)) {
    patch.excludedGroupTitles = exclusion.excludedGroupTitles
  }
  const tolerance = parseNonNegative(draft.tolerance)
  if (!tolerance.valid || tolerance.value === null || tolerance.value > MAX_TOLERANCE_PCT) {
    invalid.push('Tolerancia (%)')
  } else if (tolerance.value !== settings.tolerancePct) {
    patch.tolerancePct = tolerance.value
  }
  const channel = draft.channel.trim() === '' ? null : draft.channel.trim()
  if (channel !== settings.shortageSlackChannelId) {
    patch.shortageSlackChannelId = channel
  }
  return { patch, invalid }
}

type SettingsFormProps = {
  settings: Settings
  locations: KnownLocations
  groups: ProductGroups
}

/** The form, seeded once from the loaded settings; only what changed travels. */
const SettingsForm = ({ settings, locations, groups }: SettingsFormProps): ReactElement => {
  const { isReadOnly } = useSession()
  const utils = trpc.useUtils()
  const [draft, setDraft] = useState<Draft>(() => ({
    locationOrder: settings.locationOrder,
    excludedGroups: tickedGroups(settings, groups),
    tolerance: numberText(settings.tolerancePct),
    channel: settings.shortageSlackChannelId ?? '',
  }))
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const update = trpc.configuration.pesajeSettings.update.useMutation({
    onSuccess: () => {
      setSaved(true)
      void utils.configuration.pesajeSettings.get.invalidate()
    },
    onError: (error) => setErrorMessage(trpcMessage(error)),
  })

  const { patch, invalid } = patchOf(settings, groups, draft)
  const changed = Object.keys(patch).length > 0
  const canSave = !isReadOnly && changed && invalid.length === 0 && !update.isPending

  const edit = (next: Partial<Draft>): void => {
    setDraft({ ...draft, ...next })
    setSaved(false)
    setErrorMessage(null)
  }

  const order = draft.locationOrder
  /** The stop at `index` swapped with its neighbour: the pesaje walks the warehouse in this order. */
  const move = (index: number, neighbour: number): void =>
    edit({ locationOrder: order.map((id, position) => (position === index ? order[neighbour] : position === neighbour ? order[index] : id)) })

  const { ordered, unordered } = routeStops(order, locations.data)
  const titledInMirror = new Set(groups.map((group) => group.title))
  const titlesNotInMirror = settings.excludedGroupTitles.filter((title) => !titledInMirror.has(title))

  return (
    <form
      className={styles['admin-page__form']}
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        if (canSave) {
          update.mutate(patch)
        }
      }}
    >
      {isReadOnly ? <ReadOnlyNote /> : null}

      <fieldset className={styles['admin-page__fieldset']}>
        <legend className={styles['admin-page__legend']}>Orden de la ruta</legend>
        <p className={styles['admin-page__note']}>
          Las ubicaciones de MRPeasy en el orden en que se recorre el almacén: ordenan la lista de materiales y, dentro de cada subreceta, el detalle de una receta. «Subir» y «Bajar» lo cambian. Las que no están en la ruta van al final, por nombre.
        </p>
        <FreshnessBadge freshness={locations.freshness} />
        <ul className={styles['admin-page__checklist']} aria-label="Ubicaciones">
          {ordered.map((stop, index) => (
            <li key={stop.locationId} className={styles['admin-page__check-option']}>
              <span className={styles['admin-page__check-text']}>
                {index + 1}. {stop.name}
                {stop.known ? null : <Tag tone="warn" label="no está en el espejo" />}
              </span>
              <div className={styles['admin-page__row-actions']}>
                <Button variant="secondary" disabled={isReadOnly || index === 0} onClick={() => move(index, index - 1)} aria-label={`Subir ${stop.name}`} label="Subir" />
                <Button variant="secondary" disabled={isReadOnly || index === order.length - 1} onClick={() => move(index, index + 1)} aria-label={`Bajar ${stop.name}`} label="Bajar" />
                <Button
                  variant="secondary"
                  disabled={isReadOnly}
                  onClick={() => edit({ locationOrder: order.filter((id) => id !== stop.locationId) })}
                  aria-label={`Quitar ${stop.name} de la ruta`}
                  label="Quitar"
                />
              </div>
            </li>
          ))}
          {unordered.map((stop) => (
            <li key={stop.locationId} className={styles['admin-page__check-option']}>
              <span className={styles['admin-page__check-text']}>
                {stop.name}
                <Tag tone="neutral" label="sin orden" />
              </span>
              <div className={styles['admin-page__row-actions']}>
                <Button
                  variant="secondary"
                  disabled={isReadOnly}
                  onClick={() => edit({ locationOrder: [...order, stop.locationId] })}
                  aria-label={`Añadir ${stop.name} a la ruta`}
                  label="Añadir a la ruta"
                />
              </div>
            </li>
          ))}
        </ul>
        {ordered.length === 0 && unordered.length === 0
          ? <p className={styles['admin-page__muted']}>El espejo no conoce todavía ninguna ubicación.</p>
          : null}
      </fieldset>

      <fieldset className={styles['admin-page__fieldset']}>
        <legend className={styles['admin-page__legend']}>Grupos que no se pesan</legend>
        <p className={styles['admin-page__note']}>
          Las líneas de estos grupos de producto no salen en el pesaje ni cuentan para «Finalizar»: de entrada, «Consumibles envasado» y «Consumibles picking».
        </p>
        <ProductGroupPicker
          label="Grupos de producto que no se pesan"
          selected={draft.excludedGroups}
          disabled={isReadOnly}
          onChange={(excludedGroups) => edit({ excludedGroups })}
        />
        {titlesNotInMirror.length > 0
          ? <p className={styles['admin-page__muted']}>También por nombre, sin grupo en el espejo: {titlesNotInMirror.join(', ')}.</p>
          : null}
      </fieldset>

      <fieldset className={styles['admin-page__fieldset']}>
        <legend className={styles['admin-page__legend']}>Parámetros</legend>
        <InputText
          label="Tolerancia (%)"
          hint={`Un paquete que se aparta más de esto de lo que pide la receta avisa, sin bloquear (2). De 0 a ${MAX_TOLERANCE_PCT}.`}
          inputMode="decimal"
          value={draft.tolerance}
          disabled={isReadOnly}
          onChange={(event) => edit({ tolerance: event.target.value })}
        />
        <InputText
          label="Canal de Slack de las faltas"
          hint="El id del canal de compras (C0…), donde se avisa de una falta. Vacío, no se avisa."
          value={draft.channel}
          disabled={isReadOnly}
          onChange={(event) => edit({ channel: event.target.value })}
        />
      </fieldset>

      {invalid.length > 0 ? <p role="alert" className={styles['admin-page__error']}>Revisa: {invalid.join(', ')}.</p> : null}
      {errorMessage !== null ? <p role="alert" className={styles['admin-page__error']}>{errorMessage}</p> : null}
      {saved && !changed ? <p role="status" className={styles['admin-page__ok']}>Parámetros guardados.</p> : null}
      <div className={styles['admin-page__form-actions']}>
        <Button type="submit" disabled={!canSave} label={update.isPending ? 'Guardando…' : 'Guardar parámetros'} />
      </div>
    </form>
  )
}

/**
 * Administración → Pesaje → Parámetros: the route through the warehouse, the
 * groups that are never weighed, the tolerance and the purchasing channel
 * (`pesaje_settings`). Saving goes through `pesajeSettings.update`, which
 * leaves its line in the configuration's history.
 */
export const PesajeSettingsPage = (): ReactElement => {
  const settingsQuery = trpc.configuration.pesajeSettings.get.useQuery()
  const locationsQuery = trpc.configuration.pesajeSettings.knownLocations.useQuery()
  // The picker reads the same list (one request): the form needs it to keep the titles.
  const groupsQuery = trpc.configuration.listProductGroups.useQuery()

  let body: ReactElement
  if (settingsQuery.isPending || locationsQuery.isPending || groupsQuery.isPending) {
    body = <Spinner label="Cargando parámetros…" />
  } else if (settingsQuery.isError) {
    body = <ErrorState error={settingsQuery.error} onRetry={() => void settingsQuery.refetch()} />
  } else if (locationsQuery.isError) {
    body = <ErrorState error={locationsQuery.error} onRetry={() => void locationsQuery.refetch()} />
  } else if (groupsQuery.isError) {
    body = <ErrorState error={groupsQuery.error} onRetry={() => void groupsQuery.refetch()} />
  } else {
    body = <SettingsForm settings={settingsQuery.data} locations={locationsQuery.data} groups={groupsQuery.data.data} />
  }

  return (
    <section className={styles['admin-page']}>
      <AdminPageHeader
        title="Parámetros de pesaje"
        intro="Lo que la pantalla de pesaje de almacén lee cada vez que se calcula: entre paréntesis, el valor por defecto."
      />
      {body}
    </section>
  )
}
